"use client";

import { m, useInView } from "framer-motion";
import { useRef, useState } from "react";
import Image from "next/image";
import SectionHeader from "../sectionHeader";

interface FaqItem {
  question: string;
  answer: string;
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    items: FaqItem[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

function FaqItem({ item, index }: { item: FaqItem; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const answerId = `faq-answer-${index}`;

  return (
    <m.div
      variants={itemVariants}
      className="border border-primary"
    >
      <button
        id={`faq-question-${index}`}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={answerId}
        className="flex items-center justify-between w-full py-6 px-6 text-left gap-4"
      >
        <span className="h4 uppercase">{item.question}</span>
        <Image
          src="/images/arrow-red.svg"
          alt="Toggle answer"
          width={16}
          height={20}
          aria-hidden="true"
          draggable={false}
          className={`pointer-events-none shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-90"}`}
        />
      </button>
      {/* Keep the same answer in server HTML and the interactive accordion. */}
      <m.div
        id={answerId}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        aria-hidden={!isOpen}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-6 px-6 text-base leading-relaxed">{item.answer}</p>
      </m.div>
    </m.div>
  );
}

export default function FaqSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-12">
        <SectionHeader title={data?.title} headline={data?.headline} />
        <div className="flex flex-col w-full">
          {data?.items?.map((item, index) => (
            <FaqItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>
    </m.section>
  );
}
